import { useEffect, useState } from 'react'
import { CHART_DEFAULT_OPTIONS, SUBTITLE_FONT_SIZE, TITLE_FONT_SIZE } from '../../common/constants'
import { convertTimestamp } from '../../common/helpers'
import { formatDateForChart, roundToDecimal } from '../../common/utils'
import { ChartSubHeader } from '../ChartSubHeader'
import { CollapsibleSection } from '../CollapsibleSection'
import { SensorProduct, LINE_COLORS, DeviceInfo } from '../../common/enums'
import CanvasJSReact from '@canvasjs/react-charts'
import { Viewport } from '../../common/types'
const { CanvasJSChart } = CanvasJSReact

interface Props {
  device: DeviceInfo
  sensorType: SensorProduct
  sensorReadings: any[]
  viewport: Viewport
  isLoading: boolean
  zoomEnabled?: boolean
}

export function UnifiedChartVegetronixVH400({
  device,
  sensorType,
  sensorReadings,
  viewport,
  isLoading,
  zoomEnabled = true,
}: Props) {
  const [visible, setVisible] = useState<Record<string, boolean>>({})

  let trackDay = -1

  useEffect(() => {
    const visible: Record<string, boolean> = {}

    Object.keys(sensorReadings)
      .filter((key: string) => !key.includes('T0'))
      .map((key: string) => {
        const sensorName = sensorReadings[Number(key)].metadata.sensorName
        visible[sensorName] = true
        return null
      })

    setVisible(visible)
  }, [sensorReadings])

  const dataPoints: Record<string, any> = {}

  Object.keys(sensorReadings).forEach((key: any) => {
    const sensor = sensorReadings[key]

    const sensorName = sensor.metadata.sensorName

    const data = Object.keys(sensor.readings)
      .filter((key: string) => {
        const timestamp = parseInt(key) * 1000
        return timestamp >= viewport.minimum && timestamp <= viewport.maximum
      })
      .map((key: string) => {
        const timestamp = parseInt(key)
        return {
          label: convertTimestamp(timestamp),
          x: timestamp * 1000,
          y: sensor.readings[timestamp] as number,
        }
      })

    dataPoints[sensorName] = data
  })

  const lines =
    Object.keys(dataPoints).length > 0
      ? Object.keys(dataPoints).map((sensorPort: string, index: number) => {
          const points = dataPoints[sensorPort]
          const depth = sensorReadings.filter((sensor: any) => sensor.metadata.sensorName === sensorPort)[0].metadata
            .sensorConfig.offset
          return {
            name: `${sensorPort} (${depth}")`,
            type: 'line',
            showInLegend: true,
            xValueType: 'dateTime',
            color: LINE_COLORS[sensorPort],
            visible: visible[sensorPort],
            dataPoints: points,
          }
        })
      : []

  // const timestampMin = viewportMinimum
  // const timestampMax = viewportMaximum

  // const fieldCapacity = 26
  // const permanentWiltingPoint = 11

  // const fieldCapacityOverlay = {
  //   name: 'Field Capacity',
  //   type: 'rangeArea',
  //   xValueType: 'dateTime',
  //   markerType: 'none',
  //   color: 'yellow',
  //   lineThickness: 0,
  //   fillOpacity: 0.1,
  //   toolTipContent: `Field Capacity: ${fieldCapacity}%`,
  //   dataPoints: [
  //     { x: timestampMin, y: [fieldCapacity, 100] },
  //     { x: timestampMax, y: [fieldCapacity, 100] },
  //   ],
  // }

  // const permanentWiltingPointOverlay = {
  //   name: 'Permanent Wilting Point',
  //   type: 'rangeArea',
  //   xValueType: 'dateTime',
  //   markerType: 'none',
  //   color: 'red',
  //   lineThickness: 0,
  //   fillOpacity: 0.1,
  //   toolTipContent: `Permanent Wilting Point: ${permanentWiltingPoint}%`,
  //   dataPoints: [
  //     { x: timestampMin, y: [0, permanentWiltingPoint] },
  //     { x: timestampMax, y: [0, permanentWiltingPoint] },
  //   ],
  // }

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_SoilMoisture`,
    title: {
      text: 'Soil Moisture',
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`
        for (var i = 0; i < e.entries.length; i++) {
          content += `<span style="color:${e.entries[i].dataSeries.color}">${
            e.entries[i].dataSeries.name
          }:</span> <strong> ${roundToDecimal(e.entries[i].dataPoint.y)} % </strong><br/>`
        }
        return content
      },
    },
    axisX: {
      labelFormatter: function (e: any) {
        const date = new Date(e.value)
        const day = date.getDay()
        const dayChanged = trackDay !== day
        trackDay = day

        return formatDateForChart(date, dayChanged)
      },
      viewportMinimum: viewport?.minimum || null,
      viewportMaximum: viewport?.maximum || null,
    },
    axisY: {
      includeZero: false,
      maximum: 105,
      minimum: 0,
      title: `Moisture (%)`,
      titleFontSize: TITLE_FONT_SIZE,
      labelFontSize: TITLE_FONT_SIZE,
    },
    // data: [...lines, fieldCapacityOverlay, permanentWiltingPointOverlay],
    data: [...lines],
    legend: {
      itemclick: (e: any) => {
        setVisible((prev) => ({ ...prev, [e.dataSeries.name]: !prev[e.dataSeries.name] }))
      },
    },
  }

  const data = Object.keys(dataPoints).map((sensorPort: string) => {
    const readings = dataPoints[sensorPort]
    const lastIndex = Object.keys(readings).length - 1
    const lastTimestamp = readings[lastIndex] ? new Date(Number(readings[lastIndex]?.x)).toLocaleString() : '...'
    const lastReading = readings[lastIndex] ? Number(readings[lastIndex]?.y).toFixed(1) : ''

    const depth = sensorReadings.filter((sensor: any) => sensor.metadata.sensorName === sensorPort)[0].metadata
      .sensorConfig.offset

    return [`Port ${sensorPort}: ${sensorType}`, `${lastTimestamp}`, `Depth: ${depth}"`, `${lastReading} %`]
  })

  return (
    <CollapsibleSection title="Soil Moisture" isLoading={isLoading}>
      <ChartSubHeader data={data} />
      <CanvasJSChart options={options} />
    </CollapsibleSection>
  )
}
